/**
 * Where the artwork sits inside the visible area and how far it is magnified. `x` and `y` are the
 * artwork coordinates shown at the top left corner of the viewport, so panning moves them and the
 * zoom stays independent of the element's own size.
 *
 * Every screen to artwork conversion goes through here. The editor used to divide by a fixed zoom
 * at each call site, which is why picking, dragging, resizing and the guides each carried their own
 * copy of the same assumption.
 */
export interface Viewport {
  x: number;
  y: number;
  zoom: number;
}

/** Low enough that fit() still shows a 100 by 100 map document whole. */
export const MIN_ZOOM = 1 / 64;
export const MAX_ZOOM = 32;

export const DEFAULT_VIEWPORT: Viewport = { x: 0, y: 0, zoom: 2 };

export function clampZoom(zoom: number): number {
  if (!Number.isFinite(zoom)) return DEFAULT_VIEWPORT.zoom;
  return Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, zoom));
}

/** A stored viewport from an older project, or a partial one, made safe to use. */
export function normaliseViewport(stored: Partial<Viewport> | undefined | null): Viewport {
  if (!stored) return { ...DEFAULT_VIEWPORT };
  const x = Number.isFinite(stored.x) ? (stored.x as number) : DEFAULT_VIEWPORT.x;
  const y = Number.isFinite(stored.y) ? (stored.y as number) : DEFAULT_VIEWPORT.y;
  return { x, y, zoom: clampZoom(stored.zoom ?? DEFAULT_VIEWPORT.zoom) };
}

/** Screen point, measured inside the viewport element, to artwork pixels. */
export function toArtwork(view: Viewport, screenX: number, screenY: number): { x: number; y: number } {
  return { x: view.x + screenX / view.zoom, y: view.y + screenY / view.zoom };
}

export function toScreen(view: Viewport, artworkX: number, artworkY: number): { x: number; y: number } {
  return { x: (artworkX - view.x) * view.zoom, y: (artworkY - view.y) * view.zoom };
}

/** Artwork distance covered by a screen distance, which is what a drag delta needs. */
export function artworkDistance(view: Viewport, screenDistance: number): number {
  return screenDistance / view.zoom;
}

/** Drags the artwork with the pointer: content follows the cursor, so the origin moves against it. */
export function pan(view: Viewport, screenDx: number, screenDy: number): Viewport {
  return { ...view, x: view.x - screenDx / view.zoom, y: view.y - screenDy / view.zoom };
}

/**
 * Zooms about a point on screen, keeping whatever artwork pixel sits under it in place. Without the
 * anchor a wheel zoom drifts the artwork away from the cursor.
 */
export function zoomAt(view: Viewport, factor: number, screenX: number, screenY: number): Viewport {
  const zoom = clampZoom(view.zoom * factor);
  if (zoom === view.zoom) return view;
  const anchor = toArtwork(view, screenX, screenY);
  return { x: anchor.x - screenX / zoom, y: anchor.y - screenY / zoom, zoom };
}

/** Whole artwork centred in the viewport, at the largest zoom that leaves the padding free. */
export function fit(
  artworkWidth: number,
  artworkLength: number,
  viewportWidth: number,
  viewportHeight: number,
  padding = 0,
): Viewport {
  const usableWidth = Math.max(1, viewportWidth - padding * 2);
  const usableHeight = Math.max(1, viewportHeight - padding * 2);
  const zoom = clampZoom(Math.min(usableWidth / artworkWidth, usableHeight / artworkLength));
  return {
    x: artworkWidth / 2 - viewportWidth / 2 / zoom,
    y: artworkLength / 2 - viewportHeight / 2 / zoom,
    zoom,
  };
}

/** Artwork rectangle currently visible, for deciding what needs drawing. */
export function visibleArtwork(
  view: Viewport,
  viewportWidth: number,
  viewportHeight: number,
): { x: number; y: number; width: number; height: number } {
  return {
    x: view.x,
    y: view.y,
    width: viewportWidth / view.zoom,
    height: viewportHeight / view.zoom,
  };
}
